import { FC, useCallback, useMemo } from 'react'
import dayjs from 'dayjs'
import styles from './styles.module.scss'
import { TPostV2InmailMessageListResponse } from 'core/service/yapi/hk-web/InmailNotice/api'
import { useLocale } from 'core/components/Locale'

interface MessageItemProps {
  message: TPostV2InmailMessageListResponse['data'][number]
  onClick: (message: TPostV2InmailMessageListResponse['data'][number]) => void
  submoduleName: any
  timeType: 'today' | 'earlier'
}

const MessageItem: FC<MessageItemProps> = ({ message, onClick, submoduleName, timeType }) => {
  const t = useLocale()
  const { title, content, createdTime, read, hasLink, bizType, moduleCode, submoduleCode, dataJson } = message

  const labelList = [
    { type: 105, name: t('hotcoinWeb_v11_LatestAnnouncement') }, //最新公告
    { type: 106, name: t('hotcoinWeb_v11_SystemMaintenanceAndUpdate') }, //系统维护更新
    { type: 107, name: t('hotcoinWeb_v11_OtherAnnouncements') }, //其他公告
    { type: 108, name: t('hotcoinWeb_v11_SystemMaintenanceAndUpdate') }, //系统维护更新
    { type: 5, name: t('hotcoinWeb_v11_SystemMaintenanceAndUpdate') }, //系统维护更新
    { type: 109, name: t('hotcoinWeb_v11_WalletMaintenanceStatus') }, //钱包维护状态
  ]

  // 判断特殊标签
  const formatLabel = useCallback(() => {
    let res = submoduleName
    const findItem = labelList.find((labelItem) => labelItem.type === bizType)
    if (findItem) {
      res = findItem.name
    }
    return res
  }, [submoduleName, bizType, labelList])

  //处理dataJson
  const formatDataJson = useMemo(() => {
    try {
      const jsonData = JSON.parse(dataJson || '{}')
      console.log('dataJson转化结果:', jsonData)
      return jsonData
    } catch (err) {
      console.log('dataJson解析失败:', err)
      return null
    }
  }, [dataJson])

  //价格订阅内容格式化
  const formattPriceChangeText = (item: any) => {
    let res = ''
    if (item.alertType == 1) {
      res = t('hotcoinWeb_v11_ThePriceOfValue0', {
        value_0: item.baseCurrency + '/' + item.quoteCurrency,
        value_1: item.alertValue,
      })
    } else if (item.alertType == 2) {
      res = t('hotcoinWeb_v11_Value0PriceDropsTo', {
        value_0: item.baseCurrency + '/' + item.quoteCurrency,
        value_1: item.alertValue,
      })
    } else if (item.alertType == 3) {
      if (!item.currentPrice) {
        res = t('hotcoinWeb_v11_Value124HIncreaseExceeded', {
          value_1: item.baseCurrency + '/' + item.quoteCurrency,
          value_0: item.alertValue,
        })
      } else {
        res = t('hotcoinWeb_v11_Value024HIncreaseExceeds', {
          value_0: item.baseCurrency + '/' + item.quoteCurrency,
          value_1: item.alertValue,
          value_2: item.currentPrice,
        })
      }
    } else if (item.alertType == 4) {
      if (!item.currentPrice) {
        res = t('hotcoinWeb_v11_Value124HDeclineExceeds', {
          value_1: item.baseCurrency + '/' + item.quoteCurrency,
          value_0: item.alertValue,
        })
      } else {
        res = t('hotcoinWeb_v11_Value224HDeclineExceeds', {
          value_2: item.baseCurrency + '/' + item.quoteCurrency,
          value_1: item.alertValue,
          value_0: item.currentPrice,
        })
      }
    }
    return res
  }

  //渲染标题（行情模块渲染dataJson内容）
  const formatTitle = useMemo(() => {
    if (moduleCode !== 20) {
      return title
    }
    if (submoduleCode === 9) {
      return t('hotcoinWeb_v11_Value0PriceReminder', {
        value_0: formatDataJson.baseCurrency + '/' + formatDataJson.quoteCurrency,
      })
    }
    if (submoduleCode === 10) {
      if (formatDataJson.event === 2) {
        return formatDataJson.changeType === 1
          ? t('hotcoinWeb_v11_Value0PriceBreakthrough', {
              value_0: `${formatDataJson.baseCurrency}/${formatDataJson.quoteCurrency}`,
            })
          : t('hotcoinWeb_v11_Value0PriceFallsBelow', {
              value_0: `${formatDataJson.baseCurrency}/${formatDataJson.quoteCurrency}`,
            })
      } else {
        return formatDataJson.changeType === 1
          ? t('home_v11_ETHUSDTUp200', {
              value_0: formatDataJson.baseCurrency,
              value_1: formatDataJson.quoteCurrency,
              value_2: formatDataJson.changeMove,
            })
          : t('home_v11_ETHUSDTFell200', {
              value_0: formatDataJson.baseCurrency,
              value_1: formatDataJson.quoteCurrency,
              value_2: formatDataJson.changeMove,
            })
      }
    }
  }, [formatDataJson, submoduleCode, moduleCode, title])

  //渲染内容（行情模块渲染dataJson内容）
  const formatContent = useMemo(() => {
    if (moduleCode !== 20) {
      return content
    }
    if (submoduleCode === 9) {
      return formattPriceChangeText(formatDataJson)
    }
    if (submoduleCode === 10) {
      if (formatDataJson.event === 2) {
        return formatDataJson.changeType === 1
          ? t('hotcoinWeb_v11_ThePriceOfValue0_2', {
              value_0: `${formatDataJson.baseCurrency}/${formatDataJson.quoteCurrency}`,
              value_1: `${formatDataJson.changeMove}${formatDataJson.quoteCurrency}`,
              value_2: `${formatDataJson.spotPrice}${formatDataJson.quoteCurrency}`,
            })
          : t('hotcoinWeb_v11_ThePriceOfValue0_1', {
              value_0: `${formatDataJson.baseCurrency}/${formatDataJson.quoteCurrency}`,
              value_1: `${formatDataJson.changeMove}${formatDataJson.quoteCurrency}`,
              value_2: `${formatDataJson.spotPrice}${formatDataJson.quoteCurrency}`,
            })
      } else {
        return formatDataJson.changeType === 1
          ? t('inMail_017')
              .replace('$[tradeName]', `${formatDataJson.baseCurrency}/${formatDataJson.quoteCurrency}`)
              .replace('$[minutes]', formatDataJson.marketPeriod)
              .replace('$[range]', formatDataJson.changeMove)
              .replace('$[price]', parseFloat(formatDataJson.spotPrice))
          : t('inMail_018')
              .replace('$[tradeName]', `${formatDataJson.baseCurrency}/${formatDataJson.quoteCurrency}`)
              .replace('$[minutes]', formatDataJson.marketPeriod)
              .replace('$[range]', formatDataJson.changeMove)
              .replace('$[price]', parseFloat(formatDataJson.spotPrice))
      }
    }
  }, [formatDataJson, submoduleCode, moduleCode, content, formattPriceChangeText])

  // 处理标签显示
  // const renderLabel = () => {
  //   // 特殊的标签类型
  //   const labelTypes = [105, 106, 107, 108, 5, 109]
  //   if (labelTypes.includes(bizType)) {
  //     return null // 这些类型使用 submoduleName 作为标签
  //   }
  //   return submoduleName ? <span className={styles.messageLabel}>{submoduleName}</span> : null
  // }

  return (
    <div
      className={`${styles.messageItem} ${hasLink ? styles.linkItem : ''} ${read ? styles.read : ''}`}
      onClick={() => hasLink && onClick(message)}
    >
      {!read && <div className={styles.unreadDot} />}

      <div className={styles.messageContent}>
        <div className={styles.messageHeader}>
          {/* {renderLabel()} */}
          <div className={styles.messageTitle}>{formatTitle}</div>
        </div>

        <div className={styles.messageBody}>{formatContent}</div>

        <div className={styles.messageTime}>
          {timeType === 'today'
            ? dayjs(createdTime).format('HH:mm:ss')
            : dayjs(createdTime).format('YYYY-MM-DD HH:mm:ss')}{' '}
          {formatLabel()}
        </div>
      </div>

      {/* {hasLink && <i className={`iconfont iconjiantou ${styles.arrow}`} />} */}
    </div>
  )
}

export default MessageItem
